import { ChevronLeftIcon, StarIcon } from '@heroicons/react/outline';
import { NextSeo } from 'next-seo';
import Link from 'next/link';
import React, { useEffect, useState } from 'react';
import { sessionService } from '../../application/session';
import Layout from '../../components/layout/layout';
import SessionCard from '../../components/sessions/sessionCard';
import { routes } from '../../domain/websiteDomain';
import generateClassCollectionQuery from '../../graphql/queries/generateClassCollectionQuery';
import useUserWithRedirects from '../../hooks/useUser';
import { ClassFragment } from '../../typescript/generated/codegen';

interface UserFavouritesProps {}

const UserFavourites: React.FC<UserFavouritesProps> = () => {
  const user = useUserWithRedirects({ redirectToIfNotFound: routes.LOGIN });
  const [favouritedClasses, setFavouritedClasses] = useState<ClassFragment[]>(
    []
  );
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setFetching(true);
      const query = generateClassCollectionQuery({
        where: {
          BEID_in: user.settings?.favourited.map((fav: any) => fav.beid),
        },
        limit: 999,
      });
      const applicationSessions = sessionService();
      const sessions = await applicationSessions.getClasses(query);
      if (sessions.length) {
        setFavouritedClasses(sessions);
      }

      setFetching(false);
    };

    // Only fetch favourites if there are any associated with the user
    if (user?.settings?.favourited?.length) {
      fetchData();
    } else {
      setFavouritedClasses([]);
    }
  }, [user?.settings?.favourited]);

  return (
    <Layout classes="" title="Your Favourites">
      <NextSeo nofollow={true} noindex={true} />
      <section className="py-12 md:py-24">
        <div className="container">
          <h1 className="mb-12 text-5xl font-family-bold">Your Favourites</h1>
          <div className="mb-4">
            <Link href="/">
              <a className="flex items-center font-family-bold">
                <ChevronLeftIcon className="w-4 h-4 mr-1" /> Back to library
              </a>
            </Link>
          </div>
          {!fetching && !favouritedClasses.length && (
            <div className="justify-center p-4 bg-accent bg-opacity-10">
              No favourites to show - try adding some by clicking the{' '}
              <StarIcon className="inline w-6 h-6 mx-1" fill="currentColor" />{' '}
              on your favourite classes.
            </div>
          )}
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {favouritedClasses &&
              favouritedClasses.map(session => (
                <SessionCard
                  key={`favourite-${session.name}`}
                  session={session}
                  showTeacher
                />
              ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default UserFavourites;
